import { useState, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw, Home, CheckCircle, XCircle } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Frequent cheap poll of our backend (DB read) — reflects the callback fast.
const POLL_INTERVAL_MS = 4000;
// Safaricom gives the customer ~60s on the prompt before it fires a timeout.
// This is only a "taking longer than usual" nudge — never a verdict. Polling
// continues until the backend returns a terminal outcome.
const SAF_TIMEOUT_S = 60;
// Times (after the prompt is sent) to auto-force a Safaricom status query via the
// backend, catching a dropped/late callback without querying Safaricom every poll.
const FORCE_CHECK_TIMES_MS = [20000, 45000, 65000];

// friendly copy per backend failure_reason token — users never see raw codes.
const FAILURE_COPY = {
  cancelled: {
    title: "Payment Cancelled",
    body: "You dismissed the M-Pesa prompt. You can try again whenever you're ready.",
  },
  timeout: {
    title: "Prompt Timed Out",
    body: "The M-Pesa prompt expired before it was completed. Please try again.",
  },
  wrong_pin: {
    title: "Wrong M-Pesa PIN",
    body: "The PIN entered was incorrect. Please try again.",
  },
  insufficient_funds: {
    title: "Insufficient Funds",
    body: "There wasn't enough M-Pesa balance to complete this payment.",
  },
  failed: {
    title: "Payment Failed",
    body: "We couldn't complete the payment. Please try again.",
  },
};

const copyFor = (reason) => FAILURE_COPY[reason] || FAILURE_COPY.failed;

export const PaymentProcessingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    bookingNumber,
    bookingId,
    reference: initialReference,
    phone,
    amount,
    customerName,
    email,
  } = location.state || {};

  // "pending" | "failed"
  const [phase, setPhase] = useState("pending");
  const [failureReason, setFailureReason] = useState(null);
  const [countdown, setCountdown] = useState(SAF_TIMEOUT_S);
  const [slow, setSlow] = useState(false); // past the ~60s Safaricom window, still pending
  const [reference, setReference] = useState(initialReference || "");
  const [retrying, setRetrying] = useState(false);
  const [checking, setChecking] = useState(false);

  const pollRef = useRef(null);
  const forceTimersRef = useRef([]);
  const abortRef = useRef(null);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  /* ─── Guard ──────────────────────────────────────────────────── */
  useEffect(() => {
    if (!bookingNumber || !initialReference) {
      navigate("/", { replace: true });
    }
  }, [bookingNumber, initialReference, navigate]);

  /* ─── Stop polling ───────────────────────────────────────────── */
  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    forceTimersRef.current.forEach(clearTimeout);
    forceTimersRef.current = [];
  }, []);

  const goToSuccess = useCallback(
    (ref) => {
      stopPolling();
      navigate(`/payment-success?booking=${bookingNumber}&reference=${ref}`, {
        replace: true,
      });
    },
    [bookingNumber, navigate, stopPolling]
  );

  /* ─── Resolve one status response ────────────────────────────── */
  // Returns true if the outcome was terminal (success or failure).
  const applyStatus = useCallback(
    (data, ref) => {
      const status = data?.transaction_status;
      if (status === "success") {
        goToSuccess(ref);
        return true;
      }
      if (status && status !== "pending") {
        // cancelled / timeout / wrong_pin / insufficient_funds / failed
        stopPolling();
        setFailureReason(status);
        setPhase("failed");
        return true;
      }
      return false; // still pending
    },
    [goToSuccess, stopPolling]
  );

  /* ─── Poll until a terminal outcome ──────────────────────────── */
  const startPolling = useCallback(
    (ref) => {
      stopPolling();
      // Frequent cheap DB-read polls — reflect the callback result quickly.
      pollRef.current = setInterval(async () => {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        try {
          const res = await axios.get(`${API}/payments/verify/${ref}`, {
            signal: abortRef.current.signal,
          });
          applyStatus(res.data, ref);
        } catch (err) {
          if (axios.isCancel(err)) return;
          // network hiccup — keep polling
        }
      }, POLL_INTERVAL_MS);
      // Spaced auto-fallbacks: force a Safaricom query to catch a dropped callback.
      forceTimersRef.current = FORCE_CHECK_TIMES_MS.map((t) =>
        setTimeout(async () => {
          try {
            const res = await axios.get(`${API}/payments/verify/${ref}?force=1`);
            applyStatus(res.data, ref);
          } catch {
            // keep waiting
          }
        }, t)
      );
    },
    [applyStatus, stopPolling]
  );

  /* ─── ~60s nudge timer (does NOT decide the outcome) ─────────── */
  useEffect(() => {
    if (phase !== "pending" || slow) return;
    if (countdown <= 0) {
      setSlow(true);
      return;
    }
    const id = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [countdown, phase, slow]);

  /* ─── Start polling on mount ─────────────────────────────────── */
  useEffect(() => {
    if (reference) startPolling(reference);
    return () => {
      stopPolling();
      if (abortRef.current) abortRef.current.abort();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ─── Manual check — forces a live Safaricom query via our backend ─── */
  const handleCheckStatus = async () => {
    setChecking(true);
    try {
      const res = await axios.get(`${API}/payments/verify/${reference}?force=1`);
      applyStatus(res.data, reference);
    } catch {
      // ignore — user can try again
    } finally {
      setChecking(false);
    }
  };

  /* ─── Retry STK push (only offered after a terminal failure) ─── */
  const handleRetry = async () => {
    setRetrying(true);
    try {
      const res = await axios.post(`${API}/payments/initialize`, {
        booking_id: bookingId,
        amount,
        phone_number: phone,
      });
      if (res.data.status) {
        const newRef = res.data.reference;
        setReference(newRef);
        setFailureReason(null);
        setCountdown(SAF_TIMEOUT_S);
        setSlow(false);
        setPhase("pending");
        startPolling(newRef);
      }
    } catch {
      // stay on failed — user can try again
    } finally {
      setRetrying(false);
    }
  };

  const isPending = phase === "pending";
  const fail = phase === "failed" ? copyFor(failureReason) : null;

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-sm text-center"
      >
        {/* ── Icon / Logo area ──────────────────────────────────── */}
        <div className="relative flex items-center justify-center mb-8">
          <AnimatePresence>
            {isPending && (
              <motion.div
                key="ring"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex items-center justify-center"
              >
                <svg className="absolute w-36 h-36" viewBox="0 0 144 144" fill="none">
                  <circle cx="72" cy="72" r="64" stroke="rgba(215,172,4,0.12)" strokeWidth="6" />
                  {!slow && (
                    <motion.circle
                      cx="72" cy="72" r="64"
                      stroke="#d7ac04" strokeWidth="6" strokeLinecap="round"
                      strokeDasharray="402"
                      strokeDashoffset={402 - (402 * countdown) / SAF_TIMEOUT_S}
                      transform="rotate(-90 72 72)"
                      transition={{ duration: 1, ease: "linear" }}
                    />
                  )}
                  {slow && (
                    <circle
                      cx="72" cy="72" r="64"
                      stroke="#d7ac04" strokeWidth="6" strokeLinecap="round"
                      strokeDasharray="20 30" opacity="0.5"
                      transform="rotate(-90 72 72)"
                      className="animate-spin"
                      style={{ transformOrigin: "72px 72px" }}
                    />
                  )}
                </svg>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence mode="wait">
            {isPending ? (
              <motion.div
                key="logo"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-24 h-24 rounded-full overflow-hidden flex items-center justify-center border-2 border-empire-gold/30 bg-neutral-950 z-10"
              >
                <img src="/branding/logo-light.png" alt="Empire Dance" className="w-16 h-16 object-contain" />
              </motion.div>
            ) : (
              <motion.div
                key="failed-icon"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="w-24 h-24 rounded-full flex items-center justify-center border-2 border-red-500/40 bg-neutral-950 z-10"
              >
                <XCircle className="w-12 h-12 text-red-400" strokeWidth={1.5} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── Status text ───────────────────────────────────────── */}
        <AnimatePresence mode="wait">
          {isPending ? (
            <motion.div
              key="pending"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
            >
              <h2 className="text-2xl font-bold text-white mb-2">Waiting for Payment</h2>
              <p className="text-neutral-400 text-sm leading-relaxed px-4">
                Check your phone and enter your{" "}
                <span className="text-white font-medium">M-Pesa PIN</span> to complete the payment.
              </p>

              {amount && (
                <div className="mt-4 inline-flex items-center gap-2 bg-empire-gold/10 border border-empire-gold/30 rounded-lg px-5 py-2.5">
                  <span className="text-empire-gold font-bold text-xl">
                    KES {Number(amount).toLocaleString()}
                  </span>
                </div>
              )}

              {!slow ? (
                <div className="mt-4 space-y-3">
                  <p className="text-neutral-600 text-sm tabular-nums">{countdown}s</p>
                  <Button
                    variant="outline"
                    onClick={handleCheckStatus}
                    disabled={checking}
                    className="w-full border-neutral-700 text-neutral-300 hover:text-white h-10"
                  >
                    {checking ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Checking…</>
                    ) : (
                      <><CheckCircle className="w-4 h-4 mr-2" />Check Status</>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="mt-6 space-y-3">
                  <p className="text-neutral-500 text-sm">
                    This is taking longer than usual. If you completed the M-Pesa
                    prompt, tap Check Status — otherwise you can retry.
                  </p>
                  <Button
                    onClick={handleCheckStatus}
                    disabled={checking}
                    className="w-full btn-empire h-12"
                  >
                    {checking ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Checking…</>
                    ) : (
                      <><CheckCircle className="w-4 h-4 mr-2" />Check Status</>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleRetry}
                    disabled={retrying}
                    className="w-full border-neutral-700 text-neutral-300 hover:text-white h-10"
                  >
                    {retrying ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Sending…</>
                    ) : (
                      <><RefreshCw className="w-4 h-4 mr-2" />Retry Payment</>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => navigate("/", { replace: true })}
                    className="w-full text-neutral-400 hover:text-white h-10"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Back to Home
                  </Button>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="failed"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
            >
              <h2 className="text-2xl font-bold text-white mb-2">{fail.title}</h2>
              <p className="text-neutral-400 text-sm leading-relaxed px-4">{fail.body}</p>

              {amount && (
                <div className="mt-4 inline-flex items-center gap-2 bg-empire-gold/10 border border-empire-gold/30 rounded-lg px-5 py-2.5">
                  <span className="text-empire-gold font-bold text-xl">
                    KES {Number(amount).toLocaleString()}
                  </span>
                </div>
              )}

              <div className="mt-6 space-y-3">
                <Button onClick={handleRetry} disabled={retrying} className="w-full btn-empire h-12">
                  {retrying ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Sending…</>
                  ) : (
                    <><RefreshCw className="w-4 h-4 mr-2" />Retry Payment</>
                  )}
                </Button>

                <Button
                  variant="outline"
                  onClick={handleCheckStatus}
                  disabled={checking}
                  className="w-full border-neutral-700 text-neutral-300 hover:text-white h-10"
                >
                  {checking ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Checking…</>
                  ) : (
                    <><CheckCircle className="w-4 h-4 mr-2" />I Already Paid — Check Status</>
                  )}
                </Button>

                <Button
                  variant="ghost"
                  onClick={() => navigate("/", { replace: true })}
                  className="w-full text-neutral-400 hover:text-white h-10"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Customer & booking details ────────────────────────── */}
        <div className="mt-8 border-t border-neutral-900 pt-6 space-y-3 text-left">
          {customerName && (
            <div className="flex justify-between items-center">
              <span className="text-neutral-500 text-sm">Name</span>
              <span className="text-white font-semibold text-base">{customerName}</span>
            </div>
          )}
          {phone && (
            <div className="flex justify-between items-center">
              <span className="text-neutral-500 text-sm">Phone</span>
              <span className="text-white font-semibold text-base">{phone}</span>
            </div>
          )}
          {email && (
            <div className="flex justify-between items-center">
              <span className="text-neutral-500 text-sm">Email</span>
              <span className="text-white font-medium text-sm truncate max-w-[200px]">{email}</span>
            </div>
          )}
          {amount && (
            <div className="flex justify-between items-center">
              <span className="text-neutral-500 text-sm">Amount</span>
              <span className="text-empire-gold font-bold text-lg">KES {Number(amount).toLocaleString()}</span>
            </div>
          )}
          {bookingNumber && (
            <div className="flex justify-between items-center">
              <span className="text-neutral-500 text-sm">Ref</span>
              <span className="text-neutral-300 font-mono text-sm">{bookingNumber}</span>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};
